import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getMicStream, micErrorMessage } from "@/lib/mic";
import { track } from "@/lib/track";
import { useTheme } from "@/lib/theme";
import { toast } from "sonner";
import {
  Upload, FileText, X, ArrowLeft, Sparkles, ClipboardPaste,
  Mic, Square, Youtube, Film, AudioLines, Pause, Play,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import UpgradeNudge from "@/components/UpgradeNudge";

const TABS = [
  { key: "paste", label: "Paste text", icon: ClipboardPaste, hint: "Lecture text" },
  { key: "upload", label: "Upload", icon: Upload, hint: "PDF · DOCX · TXT" },
  { key: "record", label: "Record", icon: Mic, hint: "Voice memo" },
  { key: "media", label: "Audio / Video", icon: Film, hint: "MP3 · MP4 · WAV" },
  { key: "youtube", label: "YouTube", icon: Youtube, hint: "Paste a link" },
];

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

export default function StudyCreate() {
  const nav = useNavigate();
  const { theme } = useTheme();
  const [subjectName, setSubjectName] = useState("");
  const [title, setTitle] = useState("");
  const [tab, setTab] = useState("paste");
  const [errors, setErrors] = useState({});
  const [sampling, setSampling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  // sources
  const [files, setFiles] = useState([]);          // pdf/docx/txt
  const [audioFiles, setAudioFiles] = useState([]); // mp3/m4a/wav
  const [videoFiles, setVideoFiles] = useState([]); // mp4/mov/etc
  const [textContent, setTextContent] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");

  // recording state
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recDuration, setRecDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const previewRef = useRef(null);

  const [drag, setDrag] = useState(null); // "files" | "media" | null

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const addFiles = (list, target = "files") => {
    const arr = Array.from(list || []);
    if (target === "files") setFiles((f) => [...f, ...arr]);
    if (target === "media") {
      const audio = arr.filter((f) => f.type.startsWith("audio/") || /\.(mp3|m4a|wav|ogg|webm|mpga)$/i.test(f.name));
      const video = arr.filter((f) => f.type.startsWith("video/") || /\.(mp4|mov|mkv|avi|mpeg|mpg)$/i.test(f.name));
      const other = arr.filter((f) => !audio.includes(f) && !video.includes(f));
      setAudioFiles((a) => [...a, ...audio]);
      setVideoFiles((v) => [...v, ...video]);
      if (other.length) toast.warning(`${other.length} unsupported file(s) skipped`);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await getMicStream();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setRecordedBlob(blob);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setRecDuration(0);
      setRecordedBlob(null);
      timerRef.current = setInterval(() => setRecDuration((d) => d + 1), 1000);
    } catch (e) {
      const msg = micErrorMessage(e);
      setErrors((x) => ({ ...x, source: msg }));
      toast.error(msg, { duration: 7000 });
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const discardRecording = () => {
    setRecordedBlob(null);
    setRecDuration(0);
  };

  const hasSource = files.length > 0 || audioFiles.length > 0 || videoFiles.length > 0 ||
    textContent.trim() || youtubeUrl.trim() || recordedBlob;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    const onlyText = !files.length && !audioFiles.length && !videoFiles.length && !youtubeUrl.trim() && !recordedBlob;
    if (!title.trim()) errs.title = "Add a title for your study.";
    if (!hasSource) errs.source = "Add at least one source — paste text, upload a file, record, or add a link.";
    else if (onlyText && textContent.trim().split(/\s+/).length < 50) errs.source = "Pasted text is too short — add at least 50 words.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", title);
    if (subjectName.trim()) fd.append("subject_name", subjectName.trim());
    if (textContent.trim()) fd.append("text_content", textContent);
    if (youtubeUrl.trim()) fd.append("youtube_url", youtubeUrl.trim());
    files.forEach((f) => fd.append("files", f));
    audioFiles.forEach((f) => fd.append("audio_files", f));
    videoFiles.forEach((f) => fd.append("video_files", f));
    if (recordedBlob) {
      const ext = recordedBlob.type.includes("ogg") ? "ogg" :
                  recordedBlob.type.includes("mp4") ? "mp4" : "webm";
      fd.append("audio_files", new File([recordedBlob], `recording.${ext}`, { type: recordedBlob.type }));
    }
    try {
      const { data } = await api.post("/subcourses", fd, {
        timeout: 0, // never abort a large upload client-side
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
      });
      track("Lead", "generate_lead", { content_name: "study_created" });
      toast.success("Generating — follow the progress on the next screen");
      nav(`/app/studies/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start generation.");
    } finally {
      setSubmitting(false);
      setUploadPct(0);
    }
  };

  const trySample = async () => {
    setSampling(true);
    try {
      const { data } = await api.post("/subcourses/sample");
      nav(`/app/studies/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create the sample study.");
      setSampling(false);
    }
  };

  return (
    <div className="max-w-[760px] mx-auto px-5 md:px-8 py-10 md:py-14">
      <button
        onClick={() => nav(-1)}
        className="text-[13px] font-medium text-[#B0AEBC] hover:text-white flex items-center gap-1 mb-8"
        data-testid="back-btn"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="eyebrow">New study</div>
        <h1 className="display-2 mt-2">Compose a <span className="grad-text">study</span></h1>
        <p className="text-[15px] text-[#B0AEBC] mt-3 max-w-lg">
          Bring material from anywhere — a PDF, your own voice, a lecture video, or a YouTube link.
          Studivault transcribes, then assembles notes, summaries, flashcards, a quiz and a forecast.
        </p>
        <button type="button" onClick={trySample} disabled={sampling}
          className="mt-5 inline-flex items-center gap-2 text-[13px] font-medium text-[#B8A4FF] hover:underline disabled:opacity-50"
          data-testid="try-sample-study-btn">
          <Sparkles size={14} /> {sampling ? "Creating sample…" : "Try a sample study — ready instantly"}
        </button>
      </motion.div>

      <form onSubmit={submit} className="space-y-7 mt-10" data-testid="subcourse-create-form">
        <div>
          <label className="text-[12px] font-semibold text-[#B0AEBC] mb-1.5 block uppercase tracking-wider">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (errors.title) setErrors((x) => ({ ...x, title: null })); }}
            placeholder="e.g. Cell Membrane Transport"
            data-testid="sc-title-input"
            className="input"
            aria-invalid={!!errors.title}
          />
          {errors.title && <p className="text-[12px] text-[#F87171] mt-1.5" role="alert" data-testid="sc-title-error">{errors.title}</p>}
        </div>

        <div>
          <label className="text-[12px] font-semibold text-[#B0AEBC] mb-1.5 block uppercase tracking-wider">Subject</label>
          <input
            value={subjectName}
            onChange={(e) => setSubjectName(e.target.value)}
            data-testid="sc-subject-input"
            placeholder="e.g. Biology, History, Calculus (optional)"
            maxLength={60}
            className="input"
          />
        </div>

        <div>
          <label className="text-[12px] font-semibold text-[#B0AEBC] mb-2 block uppercase tracking-wider">Sources</label>

          {/* Tabbed source picker */}
          <div className="flex items-center gap-1 p-1 rounded-full bg-white/[0.04] border border-white/[0.06] overflow-x-auto no-scrollbar">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  data-testid={`source-tab-${t.key}`}
                  className="relative px-3.5 py-1.5 text-[12.5px] font-medium rounded-full flex items-center gap-1.5 shrink-0 transition-colors"
                  style={{ color: active ? (theme === "light" ? "#1A1A22" : "#F4F4F5") : (theme === "light" ? "#52525B" : "#A1A1AA") }}
                >
                  {active && (
                    <motion.span
                      layoutId="source-tab-pill"
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(135deg, rgba(124,92,255,0.32), rgba(90,200,250,0.20))",
                        border: "1px solid rgba(124,92,255,0.40)",
                        boxShadow: "0 0 24px -6px rgba(124,92,255,0.55)",
                      }}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <t.icon size={13} className="relative" />
                  <span className="relative">{t.label}</span>
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.22 }}
              className="mt-4"
            >
              {["record", "media", "youtube"].includes(tab) && (
                <UpgradeNudge
                  variant="inline"
                  feature={tab === "youtube" ? "YouTube import" : tab === "media" ? "Audio & video import" : "Voice recording"}
                  className="mb-4"
                />
              )}
              {tab === "upload" && (
                <DropZone
                  drag={drag === "files"}
                  setDrag={(v) => setDrag(v ? "files" : null)}
                  onFiles={(f) => addFiles(f, "files")}
                  inputId="file-upload"
                  accept=".pdf,.docx,.txt,.md"
                  testId="file-drop-zone"
                  icon={Upload}
                  title="Drop PDF, DOCX, or TXT"
                  hint="Multiple files OK"
                />
              )}

              {tab === "paste" && (
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  data-testid="sc-text-input"
                  placeholder="Paste lecture notes, an article, a chapter, lyrics, anything…"
                  className="input min-h-[200px] resize-y leading-relaxed"
                />
              )}

              {tab === "record" && (
                <RecordPanel
                  recording={recording}
                  duration={recDuration}
                  blob={recordedBlob}
                  onStart={startRecording}
                  onStop={stopRecording}
                  onDiscard={discardRecording}
                  previewRef={previewRef}
                />
              )}

              {tab === "media" && (
                <DropZone
                  drag={drag === "media"}
                  setDrag={(v) => setDrag(v ? "media" : null)}
                  onFiles={(f) => addFiles(f, "media")}
                  inputId="media-upload"
                  accept="audio/*,video/*,.mp3,.m4a,.wav,.ogg,.mp4,.mov,.mkv,.webm"
                  testId="media-drop-zone"
                  icon={AudioLines}
                  title="Drop audio or video"
                  hint="MP3 · M4A · WAV · MP4 · MOV — auto-transcribed"
                />
              )}

              {tab === "youtube" && (
                <div className="relative">
                  <Youtube size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#B8A4FF]" />
                  <input
                    type="url"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=…"
                    data-testid="sc-youtube-input"
                    className="input pl-11"
                  />
                  <p className="text-[11.5px] text-[#8E8B99] mt-2 pl-1">
                    We pull the captions / transcript. Works on any video that has subtitles enabled.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {errors.source && (
            <p className="text-[12.5px] text-[#F87171] mt-3" role="alert" data-testid="sc-source-error">{errors.source}</p>
          )}

          {/* Aggregated source pills */}
          {(files.length + audioFiles.length + videoFiles.length + (recordedBlob ? 1 : 0) + (youtubeUrl ? 1 : 0) + (textContent.trim() ? 1 : 0)) > 0 && (
            <div className="mt-4 space-y-1.5" data-testid="source-list">
              {files.map((f, i) => (
                <SourcePill key={`f-${i}`} icon={FileText} name={f.name} sub={`${(f.size / 1024).toFixed(0)}KB`} onRemove={() => setFiles((arr) => arr.filter((_, idx) => idx !== i))} testId={`remove-file-${i}`} />
              ))}
              {audioFiles.map((f, i) => (
                <SourcePill key={`a-${i}`} icon={AudioLines} name={f.name} sub={`${(f.size / 1024 / 1024).toFixed(1)}MB · audio`} onRemove={() => setAudioFiles((arr) => arr.filter((_, idx) => idx !== i))} testId={`remove-audio-${i}`} />
              ))}
              {videoFiles.map((f, i) => (
                <SourcePill key={`v-${i}`} icon={Film} name={f.name} sub={`${(f.size / 1024 / 1024).toFixed(1)}MB · video`} onRemove={() => setVideoFiles((arr) => arr.filter((_, idx) => idx !== i))} testId={`remove-video-${i}`} />
              ))}
              {recordedBlob && (
                <SourcePill icon={Mic} name="Voice recording" sub={`${fmtTime(recDuration)} · audio`} onRemove={discardRecording} testId="remove-recording" />
              )}
              {youtubeUrl.trim() && (
                <SourcePill icon={Youtube} name={youtubeUrl} sub="YouTube transcript" onRemove={() => setYoutubeUrl("")} testId="remove-youtube" />
              )}
              {textContent.trim() && (
                <SourcePill icon={ClipboardPaste} name="Pasted text" sub={`${textContent.split(/\s+/).length} words`} onRemove={() => setTextContent("")} testId="remove-text" />
              )}
            </div>
          )}
        </div>

        <motion.button
          type="submit"
          disabled={submitting}
          whileHover={{ scale: submitting ? 1 : 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="sc-generate-btn"
          className="btn btn-primary w-full py-4 text-[15px]"
        >
          {submitting ? (
            <><Sparkles size={15} className="animate-pulse" /> {uploadPct > 0 && uploadPct < 100 ? `Uploading… ${uploadPct}%` : "Generating… ~60s"}</>
          ) : (
            <><Sparkles size={15} /> Compose</>
          )}
        </motion.button>
      </form>
    </div>
  );
}

// ----- subcomponents -----

const DropZone = ({ drag, setDrag, onFiles, inputId, accept, testId, icon: Icon, title, hint }) => (
  <motion.label
    htmlFor={inputId}
    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
    onDragLeave={() => setDrag(false)}
    onDrop={(e) => { e.preventDefault(); setDrag(false); onFiles(e.dataTransfer.files); }}
    animate={{ scale: drag ? 1.02 : 1 }}
    className={`block rounded-2xl border-2 border-dashed transition-all p-10 text-center cursor-pointer ${
      drag
        ? "border-[#7C5CFF] bg-gradient-to-br from-[#7C5CFF]/15 to-[#5AC8FA]/8 shadow-[0_0_50px_-10px_rgba(124,92,255,0.6)]"
        : "border-white/15 hover:border-[#7C5CFF]/60 hover:bg-gradient-to-br hover:from-[#7C5CFF]/8 hover:to-[#5AC8FA]/4"
    }`}
    data-testid={testId}
  >
    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7C5CFF]/25 to-[#5AC8FA]/15 border border-white/10 flex items-center justify-center mx-auto mb-3" style={{ boxShadow: "0 0 24px -8px rgba(124,92,255,0.55)" }}>
      <Icon size={22} className="text-[#B8A4FF]" strokeWidth={1.8} />
    </div>
    <div className="font-semibold text-[15px]">{title}</div>
    <div className="text-[12px] text-[#8E8B99] mt-1">{hint}</div>
    <input
      id={inputId}
      type="file"
      multiple
      accept={accept}
      className="hidden"
      onChange={(e) => onFiles(e.target.files)}
      data-testid={`${testId}-input`}
    />
  </motion.label>
);

const SourcePill = ({ icon: Icon, name, sub, onRemove, testId }) => (
  <motion.div
    initial={{ opacity: 0, x: -8 }}
    animate={{ opacity: 1, x: 0 }}
    className="flex items-center justify-between bg-white/[0.04] border border-white/[0.06] rounded-xl px-3 py-2.5"
  >
    <div className="flex items-center gap-2.5 min-w-0">
      <Icon size={14} className="text-[#B8A4FF] shrink-0" />
      <span className="text-[14px] truncate">{name}</span>
      {sub && <span className="text-[11px] text-[#8E8B99] shrink-0">{sub}</span>}
    </div>
    <button
      type="button"
      onClick={onRemove}
      className="text-[#8E8B99] hover:text-[#B8A4FF] w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10"
      data-testid={testId}
    >
      <X size={14} />
    </button>
  </motion.div>
);

const RecordPanel = ({ recording, duration, blob, onStart, onStop, onDiscard, previewRef }) => {
  const url = blob ? URL.createObjectURL(blob) : null;
  return (
    <div className="rounded-2xl border-2 border-dashed border-white/15 p-8 text-center">
      <div className="relative w-20 h-20 mx-auto mb-4">
        {recording && (
          <motion.span
            className="absolute inset-0 rounded-full"
            style={{ background: "rgba(124,92,255,0.40)" }}
            animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeOut" }}
          />
        )}
        <button
          type="button"
          onClick={recording ? onStop : onStart}
          data-testid={recording ? "stop-recording-btn" : "start-recording-btn"}
          className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#7C5CFF] to-[#5AC8FA] flex items-center justify-center text-white"
          style={{ boxShadow: "0 0 36px -6px rgba(124,92,255,0.7)" }}
        >
          {recording ? <Square size={26} fill="white" /> : <Mic size={28} strokeWidth={2} />}
        </button>
      </div>
      <div className="font-semibold text-[15px] tabular-nums">
        {recording ? fmtTime(duration) : blob ? fmtTime(duration) : "0:00"}
      </div>
      <div className="text-[12px] text-[#8E8B99] mt-1">
        {recording ? "Recording…" : blob ? "Ready to attach" : "Tap to record"}
      </div>
      {blob && !recording && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <audio ref={previewRef} src={url} controls className="max-w-[260px]" data-testid="recording-preview" />
          <button
            type="button"
            onClick={onDiscard}
            data-testid="discard-recording-btn"
            className="text-[12px] font-medium text-[#B0AEBC] hover:text-[#B8A4FF] px-3 py-1.5 rounded-full hover:bg-white/8"
          >
            Discard
          </button>
        </div>
      )}
    </div>
  );
};
